import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import "./VideoGallery.css"; // Make sure to create and use this CSS file

// --- Data Definition ---
// This is the ONLY place you need to edit to add or remove videos.
const videosData = [
  {
    youtubeId: "dL3luJheLcE",
    description: "Insightful discussions on stablecoins - Mr. Giordano Cortese, First Rate Exchange Services Ltd",
  },
  {
    youtubeId: "p5ASujOCF8M",
    description:
      "There were some really interesting discussions around stablecoins – Mr. William Brewer, Link FX",
  },
  {
    youtubeId: "0s2HhkJy-IY",
    description:
      "The opportunity to engage with fellow stakeholders is invaluable – Mr. Thomas Onih, Director, Netsible Solutions Ltd",
  },
];

// --- Reusable Video Thumbnail Component ---
// This component manages its own animation state.
const VideoThumbnail = ({ youtubeId, onClick, index, colClass }) => {
  const ref = useRef(null);
  // `once: true` ensures the animation only plays once.
  // `amount: 0.3` means the animation triggers when 30% of the item is visible.
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const thumbnailVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.div
      ref={ref}
      className={`${colClass} col-sm-12 d-flex justify-content-center mb-4`}
      variants={thumbnailVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      transition={{ duration: 0.5, delay: index * 0.1 }} // Staggered delay
      onClick={onClick}
    >
      <div className="video-thumbnail-container w-100">
        <img
          src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
          alt="Video thumbnail"
          className="img-fluid gallery-thumb"
        />
        <div className="play-icon">
          <i className="fa-solid fa-play"></i>
        </div>
      </div>
    </motion.div>
  );
};

// --- Main Video Gallery Component ---
const VideoGallery = () => {
  const mainHeadingRef = useRef(null);
  const isHeadingInView = useInView(mainHeadingRef, { once: true });

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openModal = (index) => {
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const showNext = (e) => {
    e.stopPropagation(); // Prevent modal from closing when clicking the button
    setCurrentIndex((prev) => (prev + 1) % videosData.length);
  };

  const showPrev = (e) => {
    e.stopPropagation(); // Prevent modal from closing
    setCurrentIndex(
      (prev) => (prev - 1 + videosData.length) % videosData.length,
    );
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const badgeItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const containerVariants = {
    visible: { transition: { staggerChildren: 0.2 } },
  };

  // Determine column class based on video count
  const getColClass = () => {
    const count = videosData.length;
    if (count === 1) return "col-lg-8 col-md-10";
    if (count === 2) return "col-lg-6 col-md-6";
    return "col-lg-4 col-md-6";
  };

  const colClass = getColClass();

  return (
    <section
      className="service_section section_space section_decoration"
      style={{ overflowX: "hidden" }}
    >
      <div className="container">
        <div className="heading_block text-center" ref={mainHeadingRef}>
          <motion.div
            className="badge style_2 text-uppercase"
            variants={containerVariants}
            initial="hidden"
            animate={isHeadingInView ? "visible" : "hidden"}
          >
            <motion.span variants={badgeItem}>
              <i className="fa-solid fa-circle text-primary"></i> Our Gallery{" "}
              <i className="fa-solid fa-circle text-primary"></i>
            </motion.span>
          </motion.div>
          <motion.h2
            variants={headingVariants}
            initial="hidden"
            animate={isHeadingInView ? "visible" : "hidden"}
            className="heading_text mb-0"
          >
            Video Gallery 2026
          </motion.h2>
        </div>

        <div className="row align-items-center mt-5 justify-content-center">
          {videosData.map((video, index) => (
            <VideoThumbnail
              key={video.youtubeId} // YouTube IDs are great unique keys
              youtubeId={video.youtubeId}
              index={index}
              colClass={colClass}
              onClick={() => openModal(index)}
            />
          ))}
        </div>

        <div className="text-center mt-5">
          <NavLink
            className="btn bg-primary text-dark fw-bold px-5"
            to="/video-gallery-2026"
          >
            <span className="btn_label">View All Videos</span>
          </NavLink>
        </div>
      </div>

      {/* Modal for Video Player */}
      {modalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="video-responsive">
              <iframe
                width="853"
                height="450"
                src={`https://www.youtube.com/embed/${videosData[currentIndex].youtubeId}?autoplay=1&rel=0`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Embedded youtube"
              />
            </div>

            {videosData[currentIndex].description && (
              <p className="modal-description">
                {videosData[currentIndex].description}
              </p>
            )}

            <button className="modal-close" onClick={closeModal}>
              ×
            </button>
            <button className="modal-prev" onClick={showPrev}>
              ❮
            </button>
            <button className="modal-next" onClick={showNext}>
              ❯
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default VideoGallery;
